/**
 * 은행 선택 칸 — 입력칸 하나로 고르기·자동완성·직접입력을 다 받는다 (사이트 공용)
 *
 * 빈 요소만 주면 된다. 값을 읽고 넣는 일도 여기로 한다.
 *   const bank = $j.Ui.BankField.mount('#el', { items, value: '농협은행' });
 *   bank.value;           // 최종 은행명 (고른 값 또는 직접 입력한 값)
 *   bank.value = '...';   // 목록에 없는 값도 free 면 그대로 들어간다
 *   bank.close();         // 펼친 목록 닫기 — 모달이 닫힐 때 호출부가 부른다
 *
 * 옵션
 *   items        선택지. {name, type:'BANK'|'STOCK', old:'직전 이름', also:[다른 표기], logo:'symbol id'} 또는 문자열.
 *                정본은 g5_banks — 서버의 App\Services\Bank\BankService 가 이 모양으로 준다
 *   sort         가나다순 정렬 (기본 true. 영문으로 시작하는 이름은 뒤로)
 *   free         직접입력 허용 (기본 true). false 면 목록에 있는 값만 받는다 — 코드표에 묶인 화면용
 *   layout       처음 보기 방식 'list' | 'grid' (기본 'list'). 사용자가 패널에서 바꾸면 그 선택이 이 값을 이기고 저장된다
 *   layoutFixed  true 면 layout 으로 고정 — 저장된 선택을 보지 않고 전환 버튼도 없다. 선택지가 정해진 짧은 목록용(CMS 지정 은행)
 *   select       값을 hidden select 에 두는 화면용. items 는 그 option 에서 읽고(로고는 option 의 data-logo), 고르면
 *                select.val(v).trigger('change'), 코드가 select 를 바꿔도 칸 표시가 따라온다
 *   listFirst    true 면 칸을 눌러도 키패드 없이 목록만 연다. 글자는 패널의 [찾기] 를 눌러야 친다(layoutFixed 면 버튼도 없다) — 폰 화면용.
 *                키패드가 올라오면 화면 절반을 가려 정작 고를 목록이 안 보인다
 *   showOld      true 면 표형에도 옛 이름을 늘 보인다. 찾기가 없는 칸(layoutFixed + listFirst)은 이게 없으면 옛 이름을 볼 길이 없다
 *   placeholder  미선택 문구
 *   icon         false 면 아이콘을 안 그린다
 *   logoSprite   로고 묶음 SVG 주소(public/img/banks.svg). 주면 item.logo 의 symbol 을 그린다. 없는 곳은 글자 아이콘
 *   container    목록을 붙일 곳 (기본 body). 페이지 로컬 토큰을 쓰는 화면은 그 스코프 안으로.
 *                position:fixed 라 부모 overflow 에 잘리지 않는다. 모달 안이면 body 그대로 둔다
 *   onChange(v)  값이 바뀔 때
 *
 * 자동완성: 글자를 치면 앞글자 일치 → 포함 순으로 좁혀지고 첫 후보가 강조돼 Enter 로 고른다.
 * 그냥 칸을 벗어나면, 앞글자가 맞는 곳이 하나뿐이거나 그중 은행이 하나뿐일 때 그쪽으로 채운다('국' → 국민은행, '우리' → 우리은행).
 * 후보가 여럿이거나 없으면 free 는 친 글자 그대로, 아니면 이전 값으로 되돌린다.
 *
 * 옛 이름·다른 표기로도 찾는다 — '대구' → iM뱅크. 옛 이름은 목록형에 늘, 표형에는 그 이름으로 찾았을 때 '구 대구은행' 처럼 보인다.
 * 이미 저장된 옛 값은 건드리지 않는다. 칸에 그대로 보이고, 사람이 고르거나 고쳐 칠 때만 바뀐다.
 *
 * 터치에서 표만 보고 고르려면 화살표를 누른다 — 키보드가 안 올라온다. 글자 부분을 누르면 키보드가 함께 올라온다(listFirst 면 안 올라온다).
 * 키보드가 올라오면 화면 높이가 줄어 resize·scroll 이 오므로, 입력 중에는 닫지 않고 자리만 다시 잡는다.
 *
 * 탭은 증권사가 섞인 목록에서만 나온다. 검색은 탭과 무관하게 전체에서 찾는다.
 *
 * 색·치수는 `--ui-bank-field-*` 만 읽는다. 구조는 shared/components/_bank-field.scss 이고,
 * 쓰는 사이트의 main.scss 가 import 한 뒤 사이트 토큰에 잇는다.
 * CLAUDE.md 「공용 UI 컴포넌트는 사이트를 모른다」.
 */
import Store from '../Common/Store';

const K_LAYOUT = 'ui_bank_field_layout';   // 보기 방식 — 사용자가 고른 값 (.ai/state-management.md §4)
const TAB_BANK = '은행';
const TAB_STOCK = '증권사';

const GAP = 2;           // 칸에 붙여 놓는다 — 띄우면 남의 목록처럼 보인다
const PAD = 8;           // 화면 가장자리에서 띄울 여백
const MAX_H = 360;
const MIN_BELOW = 200;   // 아래 자리가 이보다 좁고 위가 더 넓으면 위로 펼친다

const ICON_CHEVRON = '<svg viewBox="0 0 12 8" aria-hidden="true"><path fill="currentColor" d="M6 8 0 0h12z"/></svg>';
const ICON_LIST = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M2 4h12M2 8h12M2 12h12"/></svg>';
const ICON_GRID = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="2" y="2" width="5" height="5" rx="1"/><rect x="9" y="2" width="5" height="5" rx="1"/><rect x="2" y="9" width="5" height="5" rx="1"/><rect x="9" y="9" width="5" height="5" rx="1"/></svg>';
const ICON_CHECK = '<svg class="ui-bank-field-check" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 8l3.5 3.5L13 5"/></svg>';

const collator = new Intl.Collator('ko');
const isHangul = (s) => /^[가-힣]/.test(s);

/** 가나다순. 영문으로 시작하는 이름(SC제일은행 등)은 한글 뒤로 */
const sortBanks = (names) => [...names].sort((a, b) =>
    (isHangul(b) - isHangul(a)) || collator.compare(a, b));

/** 글자 아이콘 — 영문으로 시작하면 앞 두 글자(SC·iM), 아니면 첫 글자 */
const monogram = (name) => {
    const latin = name.match(/^[A-Za-z]{1,2}/);
    return latin ? latin[0] : name.charAt(0);
};

/**
 * 앞글자 일치 → 포함 순, 각각 이름 → 옛 이름·다른 표기 순. was = 옛 이름으로 찾힌 곳의 그 이름.
 * meta = { 은행명: {type, old, also} }
 */
const search = (names, meta, text) => {
    const q = text.toLowerCase();
    const starts = (s) => s.toLowerCase().startsWith(q);
    const has = (s) => s.toLowerCase().includes(q);
    const aliasesOf = (n) => (meta[n].old ? [meta[n].old, ...meta[n].also] : meta[n].also);
    const namePrefix = names.filter(starts);
    const aliasPrefix = names.filter(n => !starts(n) && aliasesOf(n).some(starts));
    const nameHas = names.filter(n => !starts(n) && !aliasPrefix.includes(n) && has(n));
    // 한 글자로 옛 이름 속까지 뒤지면 엉뚱한 후보가 섞인다 ('국' → 한국씨티은행의 씨티은행)
    const aliasHas = q.length < 2 ? [] : names.filter(n => !has(n) && !aliasPrefix.includes(n) && aliasesOf(n).some(has));

    const was = {};
    [...aliasPrefix, ...aliasHas].forEach((n) => {
        if (meta[n].old && has(meta[n].old)) was[n] = meta[n].old;
    });
    return {
        prefix: [...namePrefix, ...aliasPrefix],
        all: [...namePrefix, ...aliasPrefix, ...nameHas, ...aliasHas],
        was,
    };
};

class Field {
    constructor(el, opts) {
        this.opts = {
            sort: true, free: true, layout: 'list', icon: true, placeholder: '은행 선택 또는 입력',
            container: 'body', ...opts,
        };
        this.$select = this.opts.select ? $(this.opts.select) : null;
        const items = this.$select
            ? this.$select.find('option').get().filter(o => o.value).map(o => ({ name: o.value, logo: o.getAttribute('data-logo') || '' }))
            : (this.opts.items || []);
        // 문자열만 받은 화면(select)은 이름으로 증권사를 가른다
        this.meta = {};
        items.forEach((it) => {
            const o = typeof it === 'string' ? { name: it } : it;
            this.meta[o.name] = {
                type: o.type || (/증권$/.test(o.name) ? 'STOCK' : 'BANK'),
                old: o.old || '',
                also: o.also || [],
                logo: o.logo || '',
            };
        });
        const names = Object.keys(this.meta);
        this.names = this.opts.sort ? sortBanks(names) : names;
        this.hasTabs = this.names.some(n => this._isStock(n)) && this.names.some(n => !this._isStock(n));

        this.val = '';
        this.panel = null;   // 펼쳤을 때만 있다

        this.$root = $(el).addClass('ui-bank-field').empty();
        this.$input = $('<input type="text" class="ui-bank-field-input" autocomplete="off" role="combobox" aria-autocomplete="list" aria-expanded="false" enterkeyhint="done">')
            .attr('placeholder', this.opts.placeholder)
            .appendTo(this.$root);
        this.$toggle = $('<button type="button" class="ui-bank-field-toggle" aria-label="목록 열기" tabindex="-1"></button>')
            .html(ICON_CHEVRON)
            .appendTo(this.$root);

        this._bindInput();
        this._lockTyping();

        if (this.$select) {
            this.$select.on('change', () => this._setValue(String(this.$select.val() || '')));
            this._setValue(String(this.$select.val() || ''));
        } else {
            this._setValue(this.opts.value || '');
        }
    }

    _isStock(name) {
        return !!this.meta[name] && this.meta[name].type === 'STOCK';
    }

    get value() {
        this._commit();   // 아직 칸에 커서가 있어도(Enter 저장 등) 친 글자를 반영한다
        return this.val;
    }

    set value(v) {
        this._setValue(String(v || ''));
    }

    close() {
        if (this.panel) this.panel.close();
    }

    _setValue(v) {
        this.val = (this.opts.free || this.names.includes(v)) ? v : '';
        this.$input.val(this.val);
    }

    /** 값 확정 + 바깥에 알린다 */
    _change(v) {
        const changed = v !== this.val;
        this._setValue(v);
        if (!changed) return;
        if (this.$select) this.$select.val(this.val).trigger('change');
        if (this.opts.onChange) this.opts.onChange(this.val);
    }

    /** 칸에 친 글자를 값으로 확정 */
    _commit() {
        const text = String(this.$input.val() || '').trim();
        if (text === this.val) { this.$input.val(text); return; }
        if (!text) { this._change(''); return; }

        const exact = this._canon(text);
        if (exact) { this._change(exact); return; }

        const { prefix, all } = search(this.names, this.meta, text);
        // '우리' 는 우리은행·우리투자증권 둘 다 맞지만 은행을 뜻하는 일이 대부분이라, 은행이 하나면 그쪽으로 채운다
        const prefixBanks = prefix.filter(n => !this._isStock(n));
        const only = prefix.length === 1 ? prefix[0]
            : (prefixBanks.length === 1 ? prefixBanks[0] : (all.length === 1 ? all[0] : ''));
        if (only) { this._change(only); return; }

        if (this.opts.free) this._change(text);
        else this.$input.val(this.val);   // 목록에 없는 값은 받지 않는다 — 이전 값으로
    }

    /** 이름·옛 이름·다른 표기가 정확히 맞는 목록상의 이름 (없으면 '') */
    _canon(text) {
        const t = text.toLowerCase();
        return this.names.find(n => n.toLowerCase() === t)
            || this.names.find(n => [this.meta[n].old, ...this.meta[n].also].some(a => a && a.toLowerCase() === t))
            || '';
    }

    /** listFirst — 칸이 포커스를 받지 않게 잠근다. 눌림은 칸 전체(root)가 받아 목록만 연다 */
    _lockTyping() {
        if (!this.opts.listFirst) return;
        this.$input.prop('readonly', true);
        this.$root.addClass('is-list-first').removeClass('is-typing');
    }

    /** [찾기] — 잠금을 풀고 키패드를 올린다. 누른 그 순간에 포커스를 줘야 폰에서 키패드가 뜬다 */
    _startTyping() {
        this.$input.prop('readonly', false);
        this.$root.addClass('is-typing');
        this.$input[0].focus();
    }

    _bindInput() {
        const $i = this.$input;

        if (this.opts.listFirst) {
            this.$root.on('click', (e) => {
                if (this.$root.hasClass('is-typing') || $(e.target).closest('.ui-bank-field-toggle').length) return;
                this.panel ? this.close() : this._open();
            });
        }

        $i.on('pointerdown', () => { if (!this.panel) this._open(); });
        $i.on('focus', () => { if (!this.panel) this._open(); });
        $i.on('input', () => {
            if (!this.panel) this._open();
            this.panel.search(String($i.val() || ''));
        });
        $i.on('blur', () => {
            this._commit();
            this.close();
        });

        // 포커스를 뺏지 않는다 — 입력 중에 화살표를 눌러도 blur 로 확정되지 않게
        this.$toggle.on('mousedown', (e) => e.preventDefault());
        this.$toggle.on('click', () => { this.panel ? this.close() : this._open(); });

        $i.on('keydown', (e) => {
            if (!this.panel) {
                if (e.key === 'ArrowDown') { e.preventDefault(); this._open(); }
                return;
            }
            if (e.key === 'Escape') {
                e.stopPropagation();   // 문서까지 올라가면 바깥 모달까지 닫힌다
                $i.val(this.val);
                this.close();
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                this.panel.move(e.key === 'ArrowDown' ? 1 : -1);
            } else if (e.key === 'Enter') {
                e.preventDefault();    // 폼 제출·모달 확인으로 번지지 않게
                e.stopPropagation();
                this.panel.enter();
            }
        });
    }

    _layout() {
        const saved = this.opts.layoutFixed ? '' : Store.local.get(K_LAYOUT);
        if (saved === 'list' || saved === 'grid') return saved;
        return this.opts.layout === 'grid' ? 'grid' : 'list';
    }

    _iconHtml($item, name) {
        if (!this.opts.icon) return;
        const $ic = $('<span class="ui-bank-field-icon" aria-hidden="true"></span>');
        const logo = this.meta[name].logo;
        if (logo && this.opts.logoSprite) {
            const NS = 'http://www.w3.org/2000/svg';
            const svg = document.createElementNS(NS, 'svg');
            svg.setAttribute('viewBox', '0 0 24 24');
            const use = document.createElementNS(NS, 'use');
            use.setAttribute('href', `${this.opts.logoSprite}#${logo}`);
            svg.appendChild(use);
            $ic.addClass('has-logo').append(svg);
        } else {
            $ic.text(monogram(name));
        }
        $item.append($ic);
    }

    _open() {
        BankField.closeActive();

        let layout = this._layout();
        const current = this._canon(this.val);   // 옛 값으로 저장돼 있어도 지금 이름에 표시한다
        let tab = this._isStock(current) ? TAB_STOCK : TAB_BANK;
        let query = '';
        let active = -1;   // 강조된 칸 (자동완성 후보·화살표)

        const $p = $('<div class="ui-bank-field-panel"></div>');
        const $head = $('<div class="ui-bank-field-head"></div>').appendTo($p);
        const $tabs = $('<div class="ui-bank-field-tabs" role="tablist"></div>').appendTo($head);
        if (this.hasTabs) {
            [TAB_BANK, TAB_STOCK].forEach((t) => {
                $('<button type="button" class="ui-bank-field-tab" role="tab"></button>').text(t).attr('data-tab', t).appendTo($tabs);
            });
        }
        if (this.opts.listFirst && !this.opts.layoutFixed) {
            $('<button type="button" class="ui-bank-field-find"></button>').text(this.opts.free ? '찾기·직접 입력' : '찾기').appendTo($head);
        }
        const $views = $('<div class="ui-bank-field-views" role="group" aria-label="보기 방식"></div>');
        if (!this.opts.layoutFixed) $views.appendTo($head);
        [['list', '목록형', ICON_LIST], ['grid', '표형', ICON_GRID]].forEach(([key, label, icon]) => {
            $('<button type="button" class="ui-bank-field-view"></button>')
                .attr({ 'data-view': key, 'aria-label': label, title: label })
                .html(icon)
                .appendTo($views);
        });
        const $body = $('<div class="ui-bank-field-body" role="listbox"></div>').appendTo($p);

        let found = { all: [], was: {} };
        const visible = () => {
            const q = query.trim();
            if (q) return found.all;
            if (!this.hasTabs) return this.names;
            return this.names.filter(n => this._isStock(n) === (tab === TAB_STOCK));
        };

        const markActive = () => {
            const $cells = $body.find('.ui-bank-field-item').removeClass('is-active');
            if (active >= 0) {
                const el = $cells.eq(active).addClass('is-active')[0];
                if (el) el.scrollIntoView({ block: 'nearest' });
            }
        };

        const paint = () => {
            const vals = visible();
            const searching = !!query.trim();

            $p.toggleClass('is-grid', layout === 'grid').toggleClass('is-list', layout !== 'grid');
            $tabs.css('visibility', searching ? 'hidden' : '');
            $tabs.find('.ui-bank-field-tab').each(function () {
                const on = $(this).attr('data-tab') === tab;
                $(this).toggleClass('is-on', on).attr('aria-selected', on ? 'true' : 'false');
            });
            $views.find('.ui-bank-field-view').each(function () {
                const on = $(this).attr('data-view') === layout;
                $(this).toggleClass('is-on', on).attr('aria-pressed', on ? 'true' : 'false');
            });

            $body.empty();
            if (!vals.length) {
                $('<div class="ui-bank-field-empty"></div>')
                    .text(this.opts.free ? '목록에 없어요. 입력한 그대로 저장돼요' : '일치하는 곳이 없어요')
                    .appendTo($body);
            }
            vals.forEach((name) => {
                const on = name === current;
                const $item = $('<button type="button" class="ui-bank-field-item" role="option"></button>')
                    .attr('data-val', name)
                    .attr('aria-selected', on ? 'true' : 'false')
                    .toggleClass('is-sel', on);
                this._iconHtml($item, name);
                const $name = $('<span class="ui-bank-field-name"></span>').text(name);
                // 목록형은 자리가 넉넉해 옛 이름을 늘 보여준다. 표형은 옛 이름으로 찾았을 때만
                const was = (layout === 'grid' && !this.opts.showOld) ? (searching && found.was[name]) : this.meta[name].old;
                if (was) $name.append($('<span class="ui-bank-field-was"></span>').text(`구 ${was}`));
                $item.append($name);
                if (on) $item.append(ICON_CHECK);
                $item.appendTo($body);
            });
            active = searching && vals.length ? 0 : -1;
            markActive();
        };

        const place = () => {
            const b = this.$root[0];
            const r = b.getBoundingClientRect();
            // 글자크기 확대가 body zoom 인 화면에서는 측정값은 확대된 px, 인라인 좌표는 확대 전 px 로
            // 해석돼 목록이 밀린다. 배율을 렌더폭 ÷ 레이아웃폭으로 재서 맞춘다(확대가 없으면 1).
            const z = b.offsetWidth ? (r.width / b.offsetWidth) : 1;
            const top = r.top / z, bottom = r.bottom / z;
            const vh = window.innerHeight / z;
            const below = vh - PAD - (bottom + GAP);
            const above = (top - GAP) - PAD;
            const up = below < MIN_BELOW && above > below;
            $p.css({
                left: Math.round(r.left / z),
                width: Math.round(r.width / z),
                top: up ? 'auto' : Math.round(bottom + GAP),
                bottom: up ? Math.round(vh - top + GAP) : 'auto',
                maxHeight: Math.min(MAX_H, Math.max(120, Math.round(up ? above : below))),
            });
        };

        const close = () => {
            $p.remove();
            this.$root.removeClass('is-open');
            this.$input.attr('aria-expanded', 'false');
            $(document).off('pointerdown.uibankfield');
            document.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', place);
            this.panel = null;
            BankField._active = null;
            this._lockTyping();
        };

        // 뒤 화면이 움직이면 닫는다. 입력 중이면 키보드가 올라오며 생긴 스크롤일 수 있어 자리만 다시 잡는다
        const onScroll = (e) => {
            if ($p[0].contains(e.target)) return;
            if (document.activeElement === this.$input[0]) place(); else close();
        };

        const pick = (name) => {
            close();
            this._change(name);
            this.$input.val(this.val);
        };

        // 패널을 눌러도 칸의 포커스를 뺏지 않는다 — 안 그러면 blur 가 먼저 와서 고르기 전에 닫힌다
        $p.on('mousedown', (e) => e.preventDefault());
        $p.on('click', '.ui-bank-field-item', (e) => pick(String($(e.currentTarget).attr('data-val'))));
        $p.on('click', '.ui-bank-field-find', () => this._startTyping());
        $p.on('click', '.ui-bank-field-tab', (e) => { tab = $(e.currentTarget).attr('data-tab'); paint(); });
        $p.on('click', '.ui-bank-field-view', (e) => {
            layout = $(e.currentTarget).attr('data-view');
            Store.local.set(K_LAYOUT, layout);
            paint();
        });

        // 모달 패널이 overflow:hidden 이라 안에 못 넣는다 — 패널 바로 위 z-index 로 띄운다
        const panelZ = parseInt(this.$root.closest('.ui-modal').css('z-index'), 10);
        if (panelZ) $p.css('z-index', panelZ + 1);

        $p.appendTo(this.opts.container);
        paint();
        place();
        this.$root.addClass('is-open');
        this.$input.attr('aria-expanded', 'true');

        this.panel = {
            close,
            search: (text) => {
                query = text;
                found = query.trim() ? search(this.names, this.meta, query.trim()) : { all: [], was: {} };
                paint();
            },
            move: (step) => {
                const count = $body.find('.ui-bank-field-item').length;
                if (!count) return;
                active = (active + step + count) % count;
                markActive();
            },
            enter: () => {
                const el = $body.find('.ui-bank-field-item').get(active);
                if (el) pick(String($(el).attr('data-val')));
                else { close(); this._commit(); }
            },
        };
        BankField._active = this.panel;

        const sel = $body.find('.is-sel')[0];
        if (sel) sel.scrollIntoView({ block: 'center' });

        // 지금 이 클릭이 그대로 이어지지 않게 다음 틱에 건다. scroll 은 capture —
        // 시트 본문 스크롤은 document 까지 버블하지 않는다.
        const panel = this.panel;
        setTimeout(() => {
            if (BankField._active !== panel) return;
            $(document).on('pointerdown.uibankfield', (e) => {
                const $t = $(e.target);
                if ($t.closest($p).length || $t.closest(this.$root).length) return;
                close();
            });
            document.addEventListener('scroll', onScroll, true);
            window.addEventListener('resize', place);
        }, 0);
    }
}

export default class BankField {
    static _active = null;   // 목록은 화면에 하나만

    static closeActive() {
        if (BankField._active) BankField._active.close();
    }

    mount = (el, opts = {}) => new Field(el, opts);
}
